import actions from '@ehi/global-marketing-interface/lib/actions';
import services from '@ehi/global-marketing-interface/lib/services';
import { config } from 'utils/config';
import { gmiRequest } from 'actions/request';
import MODAL from 'constants/modals';
import { gmaRedirect } from 'utils/redirect';
import { clearModalQueue } from 'actions/modal/clearModalQueue';
import { openModal } from 'actions/modal/openModal';
import { stashAnalyticsEvent } from 'utils/analytics';
import RENTAL from 'constants/rental';

/**
 * @name rentalRetrieveAndConfirm
 * @desc Retrieves and confirm a return
 * @returns {Object}
 */
export const rentalRetrieveAndConfirm = () => (dispatch) =>
  dispatch(
    gmiRequest({
      action: actions.request({
        service: services.gma.rentalTicket.retrieveAndConfirm,
      }),
      loadingOverlay: true,
      timeout: true,
      onComplete: (response) => {
        // making sure that response was successful before firing analytics
        if (response?.session?.rental) {
          stashAnalyticsEvent('justConfirmed', 'true');
        }
        return response;
      },
    })
  );

/**
 * @name  modifyRental - uses retrieveAndCheckEligibility without tokenNumber
 * @desc Retrieves and confirm a return
 * @returns {Object}
 */
export const modifyRental = () => (dispatch) =>
  dispatch(
    gmiRequest({
      action: actions.request({
        method: 'GET',
        service: () => ({
          method: 'GET',
          origin: config.getUrl('gmaUrl'),
          endpoint: 'rentalTicket/retrieveAndCheckEligibility',
        }),
        redirect: false,
        onComplete: (response) => {
          if (response.messages) {
            dispatch(openModal(MODAL.RENTAL_NOT_ELIGIBLE_FOR_MODIFICATION_MODAL));
          }
          return response;
        },
      }),
      loadingOverlay: true,
      timeout: true,
    })
  );

/**
 * @name modifyDetails
 * @desc Returns details for modify rental
 * @returns {Object}
 */
export const rentalModifyDetails = () => (dispatch) =>
  dispatch(
    gmiRequest({
      action: actions.request({
        service: services.gma.rentalTicket.modifyDetails,
      }),
      loadingOverlay: true,
    })
  );

/* Handles interaction with the Modify Retrurn CTA
 * @name retrieveAndCheckEligiblity
 * @param {string} ticketNumber - Rental ticket number

 * @returns {Function} - A function to retrieve a rental by ticket number
 */

export const retrieveAndCheckEligibility = (ticketNumber) => (dispatch) =>
  dispatch(
    gmiRequest({
      action: actions.request({
        service: services.gma.rentalTicket.retrieveAndCheckEligibility,
        data: {
          ticketNumber,
        },
      }),
      loadingOverlay: true,
      onComplete: (response) => {
        if (response.messages) {
          dispatch(
            openModal(MODAL.RENTAL_NOT_ELIGIBLE_FOR_MODIFICATION_MODAL, { props: { rentalTicketNumber: ticketNumber } })
          );
        }
        return response;
      },
    })
  );

/**
 * @name getRentalTermsConfig
 * @desc terms and conditions called on click of rental termss
 * @returns {Object}
 */
export const getRentalTermsConfig = () => (dispatch) =>
  dispatch(
    gmiRequest({
      action: actions.request({
        method: 'GET',
        service: () => ({
          method: 'GET',
          origin: config.getUrl('gmaUrl'),
          endpoint: 'rentalTicket/termsAndConditions',
        }),
        loadingOverlay: true,
        redirect: false,
        onComplete: (response) => {
          if (!response.messages) {
            return response.rental_terms_and_conditions;
          }
          return response;
        },
      }),
      timeout: true,
    })
  );

/**
 * @name retrieveTicketNumber
 * @desc Retrieves and ticket details
 * @returns {Object}
 */
export const rentalRetrieveTicketNumber =
  (ticketNumber, redirect = true) =>
  (dispatch) =>
    dispatch(
      gmiRequest({
        action: actions.request({
          service: services.gma.rentalTicket.retrieveTicketNumber,
          data: {
            ticketNumber,
          },
        }),
        loadingOverlay: true,
        redirect,
      })
    );

/**
 * @name initiate
 * @param {object} payload - Rental Initiate Payload
 *
 * @returns {Object} - Retrieves ticket details
 */

export const initiate = (payload, callback) => (dispatch) =>
  dispatch(
    gmiRequest({
      action: actions.request({
        service: services.gma.rentalTicket.modifyInitiate,
        data: {
          payload,
        },
      }),
      loadingOverlay: true,
      redirect: false,
      onComplete: (response) => {
        if (!response.messages) {
          if (callback && typeof callback === 'function') {
            callback();
          }
          gmaRedirect(response.go_to);
          return null;
        }

        return response;
      },
    })
  );

/**
 * @name commit

 * @returns {Object} - Commit the confirm/modify flow updates
 */

export const commit = () => (dispatch) =>
  dispatch(
    gmiRequest({
      action: actions.request({
        service: services.gma.rentalTicket.commit,
      }),
      loadingOverlay: true,
      redirect: false,
      onComplete: (response) => {
        if (!response.messages) {
          gmaRedirect(response.go_to);
          return null;
        }

        return response;
      },
    })
  );

/**
 * @name abortModifyRental

 * @returns { session, go_to } object
 */

export const abortModifyRental =
  ({ redirect = true } = {}) =>
  (dispatch) =>
    dispatch(
      gmiRequest({
        action: actions.request({
          service: services.gma.rentalTicket.abortModify,
        }),
        redirect,
        loadingOverlay: true,
        onComplete: (response) => {
          dispatch(clearModalQueue({ skipAnalytics: true }));
          if (!response.messages) {
            const goTo = structuredClone(response.go_to);
            if (goTo?.children?.[0] === 'UPDATED' || goTo?.children?.[0] === 'CONFIRMED') {
              const { RENTAL_VIEWS } = RENTAL;
              goTo.children[0] = RENTAL_VIEWS.DETAILS[0].replace(/^\/+/, '');
              return gmaRedirect(goTo);
            }
            return response?.go_to && gmaRedirect(response.go_to);
          }
          return response;
        },
      })
    );

/**
 * @name upcomingRental
 * @desc upcomingRental
 * @returns {Object}
 */
export const upcomingRental = () => (dispatch) =>
  dispatch(
    gmiRequest({
      action: actions.request({
        service: services.gma.rentalTicket.upcomingRental,
        data: {},
      }),
      loadingOverlay: true,
    })
  );
